import React from 'react'
import { css, SerializedStyles } from '@emotion/react'
import { colors } from 'plume-ui'
import { Transition } from 'react-transition-group'
import { TransitionStatus } from 'react-transition-group/Transition'

export type FilterSelectOptionsProps<OptionType = string> = {
  options: readonly OptionType[]
  labels?: readonly string[]
  selected: OptionType | OptionType[]
  onSelect: (option: OptionType) => void
  isSelectedFn?: (selected: OptionType, option: OptionType) => boolean
  isMultiple?: boolean
  unselectOption?: OptionType
  isUnselected?: boolean
  isOpen: boolean
  children?: React.ReactNode
}

export const FilterSelectOptions = <T extends any>(
  props: FilterSelectOptionsProps<T>
) => {
  const labelOrOptions = props.labels ?? props.options

  return (
    <Transition
      in={props.isOpen}
      timeout={{
        enter: 100,
        exit: 100,
      }}
    >
      {(transitionState) => (
        <div
          css={css`
            padding: 8px 0;
            box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
            border-radius: 2px;
            min-width: 165px;
            background: ${colors.white00};
            z-index: 1;
            ${transitionCSS(transitionState)};
          `}
        >
          <ul
            className="filter-option-list"
            css={css`
              margin: 0;
              padding: 0;
            `}
          >
            {(labelOrOptions as any[]).map((label, i) => {
              const isSelected = props.isMultiple
                ? (props.selected as (T | null)[])?.includes(
                    props.options[i]
                  ) ||
                  ((props.selected as (T | null)[])?.length === 0 &&
                    props.unselectOption === props.options[i])
                : props.isSelectedFn
                  ? props.isSelectedFn(props.selected as T, props.options[i])
                  : props.options[i] === props.selected

              return (
                <li
                  key={i}
                  onClick={() => props.onSelect(props.options[i])}
                  css={css`
                    padding: 9px 40px;
                    color: ${colors.text};
                    font-size: 14px;
                    transition: 0.3s;
                    position: relative;
                    user-select: none;

                    &:hover {
                      background-color: rgba(0, 0, 0, 0.04);
                      cursor: pointer;
                    }

                    ${isSelected &&
                    `
                      &:before {
                        content: '';
                        background: ${colors.cyan50};
                        position: absolute;
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        left: 23px;
                        top: 14px;
                      } 
                    `}
                  `}
                >
                  {label}
                </li>
              )
            })}
          </ul>
          {props.children}
        </div>
      )}
    </Transition>
  )
}

const transitionCSS = (
  status: TransitionStatus
): SerializedStyles | undefined => {
  switch (status) {
    case 'entering':
    case 'entered':
      return css`
        transition: opacity 100ms ease-in;
        opacity: 1;
        display: block;
      `
    case 'exiting':
    case 'exited':
      return css`
        transition: opacity 100ms ease-out;
        opacity: 0;
        display: none;
      `
    default:
      return undefined
  }
}
